/**
 * Risk Scores Card Component
 * Displays user risk scores with visual indicators
 */
import React from 'react';
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  LinearProgress,
  Alert
} from '@mui/material';
import { Security as SecurityIcon } from '@mui/icons-material';

function RiskScoresCard({ riskData, loading }) {
  if (loading) {
    return <LinearProgress />;
  }

  if (!riskData) {
    return (
      <Alert severity="info">
        No risk scoring data available. Run analytics to generate risk scores.
      </Alert>
    );
  }

  const getRiskColor = (score) => {
    if (score >= 0.7) return 'error';
    if (score >= 0.4) return 'warning';
    return 'success';
  };

  const getRiskLabel = (score) => {
    if (score >= 0.7) return 'High Risk';
    if (score >= 0.4) return 'Medium Risk';
    return 'Low Risk';
  };

  const formatScore = (score) => {
    return (score * 100).toFixed(1) + '%';
  };

  return (
    <Box>
      <Box sx={{ mb: 3 }}>
        <Typography variant="h6" gutterBottom>
          Risk Score Analysis
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Analyzes access patterns, behavior patterns, and similarity to identify high-risk users.
        </Typography>
      </Box>

      {/* Summary Stats */}
      <Box sx={{ mb: 3, display: 'flex', gap: 3 }}>
        <Box>
          <Typography variant="body2" color="text.secondary">
            Users Analyzed
          </Typography>
          <Typography variant="h5">
            {riskData.users_analyzed}
          </Typography>
        </Box>
        <Box>
          <Typography variant="body2" color="text.secondary">
            High Risk Users
          </Typography>
          <Typography variant="h5" color="error.main">
            {riskData.high_risk_users?.length || 0}
          </Typography>
        </Box>
        <Box>
          <Typography variant="body2" color="text.secondary">
            Risk Scores Updated
          </Typography>
          <Typography variant="h5">
            {riskData.risk_scores_updated}
          </Typography>
        </Box>
      </Box>

      {/* High Risk Users Table */}
      {riskData.high_risk_users && riskData.high_risk_users.length > 0 ? (
        <>
          <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>
            High Risk Users
          </Typography>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>User</TableCell>
                  <TableCell>Email</TableCell>
                  <TableCell align="right">Overall Risk</TableCell>
                  <TableCell align="right">Access Risk</TableCell>
                  <TableCell align="right">Behavior Risk</TableCell>
                  <TableCell>Status</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {riskData.high_risk_users.map((user) => (
                  <TableRow key={user.user_id}>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <SecurityIcon fontSize="small" color={getRiskColor(user.overall_risk)} />
                        <Typography variant="body2">
                          {user.display_name || user.user_id}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell>{user.email || '-'}</TableCell>
                    <TableCell align="right">
                      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end' }}>
                        <Typography variant="body2" fontWeight="bold">
                          {formatScore(user.overall_risk)}
                        </Typography>
                        <LinearProgress
                          variant="determinate"
                          value={user.overall_risk * 100}
                          color={getRiskColor(user.overall_risk)}
                          sx={{ width: 100, mt: 0.5 }}
                        />
                      </Box>
                    </TableCell>
                    <TableCell align="right">
                      {formatScore(user.access_risk)}
                    </TableCell>
                    <TableCell align="right">
                      {formatScore(user.behavior_risk)}
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={getRiskLabel(user.overall_risk)}
                        color={getRiskColor(user.overall_risk)}
                        size="small"
                      />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </>
      ) : (
        <Alert severity="success" sx={{ mt: 3 }}>
          <Typography variant="body1">
            No high-risk users detected! All users are within acceptable risk thresholds.
          </Typography>
        </Alert>
      )}
    </Box>
  );
}

export default RiskScoresCard;
